// Pure derive helpers for the Stuck Refund Workflow dashboard.
import { StuckFilters, Breakdown, HistoryPoint, TrendDelta } from './types';

/** Filter rows by any non-empty facet selections. */
export function applyFilters<T>(
    rows: T[],
    filters: StuckFilters,
    facetFields: Array<[string, string, keyof T]>
): T[] {
    return rows.filter((row) => {
        for (const [key, , field] of facetFields) {
            const sel = filters.facets[key];
            if (sel && sel.length > 0 && !sel.includes(String(row[field]))) return false;
        }
        return true;
    });
}

/** Group rows by a key, summing an optional weight (default: row count). */
export function buildBreakdown<T>(
    rows: T[],
    keyFn: (row: T) => string,
    weightFn?: (row: T) => number
): Breakdown {
    const map = new Map<string, number>();
    for (const row of rows) {
        const k = keyFn(row) || '—';
        map.set(k, (map.get(k) || 0) + (weightFn ? weightFn(row) : 1));
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
}

/** Distinct sorted values of a field over rows (for facet option lists). */
export function distinctValues<T>(rows: T[], field: keyof T): string[] {
    const set = new Set<string>();
    rows.forEach((r) => set.add(String(r[field])));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export const pct = (n: number, d: number): number => (d ? (100 * n) / d : 0);

/** Build a CSV string from column headers and row records. */
export function toCsv(headers: string[], rows: Array<Record<string, unknown>>, keys: string[]): string {
    const esc = (v: unknown) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(esc).join(',')];
    rows.forEach((r) => lines.push(keys.map((k) => esc(r[k])).join(',')));
    return lines.join('\n');
}

export function downloadCsv(filename: string, csv: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/** Compute WoW/MoM-style deltas of totalStuck against N-day-ago snapshots. */
export function buildTrends(history: HistoryPoint[]): TrendDelta[] {
    if (!history.length) return [];
    const sorted = [...history].sort((a, b) => a.day.localeCompare(b.day));
    const cur = sorted[sorted.length - 1];
    // Look up the snapshot ~N calendar days before cur.day (closest on/before that date),
    // not by array position — history has gaps on days the generator didn't run.
    const at = (daysAgo: number): HistoryPoint | null => {
        const target = new Date(cur.day + 'T00:00:00Z');
        target.setUTCDate(target.getUTCDate() - daysAgo);
        const targetDay = target.toISOString().slice(0, 10);
        let best: HistoryPoint | null = null;
        for (const s of sorted) {
            if (s.day <= targetDay) best = s;
            else break;
        }
        return best;
    };
    const mk = (label: string, past: HistoryPoint | null): TrendDelta => {
        if (!past) return { label, current: cur.totalStuck, past: null, delta: null, pct: null };
        const delta = cur.totalStuck - past.totalStuck;
        return { label, current: cur.totalStuck, past: past.totalStuck, delta, pct: pct(delta, past.totalStuck) };
    };
    return [mk('WoW', at(7)), mk('MoM', at(30)), mk('YoY', at(365))];
}
